import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';

const SEARCH_LIMIT = 10;

/**
 * Firestore prefix search for campaigns by internal name or title.
 * Requires composite indexes on (companyId, _searchInternalName) and (companyId, _searchTitle).
 *
 * @param {string} companyId - Company ID to scope search
 * @param {string} term - Search term (will be lowercased)
 * @returns {Promise<Array<{id: string, ...}>>} Deduplicated campaigns matching prefix
 */
export async function searchCampaignsByPrefix(companyId, term) {
  if (!companyId || !term || typeof term !== 'string') {
    return [];
  }

  const normalizedTerm = term.toLowerCase().trim();
  if (!normalizedTerm) return [];

  const suffix = normalizedTerm + '\uf8ff';
  const campaignsRef = collection(db, 'campaign');

  try {
    const [byInternalName, byTitle] = await Promise.all([
      getDocs(
        query(
          campaignsRef,
          where('companyId', '==', companyId),
          where('_searchInternalName', '>=', normalizedTerm),
          where('_searchInternalName', '<=', suffix),
          limit(SEARCH_LIMIT)
        )
      ),
      getDocs(
        query(
          campaignsRef,
          where('companyId', '==', companyId),
          where('_searchTitle', '>=', normalizedTerm),
          where('_searchTitle', '<=', suffix),
          limit(SEARCH_LIMIT)
        )
      ),
    ]);

    const seen = new Set();
    const campaigns = [];

    const addUnique = (doc) => {
      const data = { id: doc.id, ...doc.data() };
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        campaigns.push(data);
      }
    };

    byInternalName.docs.forEach(addUnique);
    byTitle.docs.forEach(addUnique);

    // Sort by createdAt desc, limit
    campaigns.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
      return bTime - aTime;
    });

    return campaigns.slice(0, SEARCH_LIMIT);
  } catch (err) {
    // Index may not exist yet - Firebase will log the required index in console
    console.error('Campaign search error:', err);
    return [];
  }
}
